import React from 'react';
import { Alert, Badge, Card, CardBody, Col, Progress, Row, Spinner } from 'reactstrap';
import NumberFormat from 'react-number-format';
import FalconCardHeader from '../../../common/FalconCardHeader';
import { ADOPTION_STEP_LABELS } from './adoptionLabels';

const formatNumber = (value) => <NumberFormat value={value} displayType="text" thousandSeparator />;

const percentOf = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

/**
 * Una etapa del embudo: cuántos condominios la cumplen sobre los
 * registrados. Al tocarla, la tabla de abajo muestra los que NO la cumplen,
 * que son a los que hay que activar.
 */
const StageCard = ({ stage, title, value, registered, note, color, selected, loading, onSelect }) => {
  const percent = percentOf(value, registered);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(stage)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(stage)}
      className={`card h-100 shadow-none border ${selected ? 'border-primary' : ''}`}
      style={{ cursor: 'pointer' }}
    >
      <CardBody className="d-flex flex-column">
        <h6 className="text-600 mb-2">{title}</h6>
        <div className={`display-4 fs-4 mb-1 font-weight-normal text-sans-serif text-${color}`}>
          {loading ? <Spinner size="sm" /> : formatNumber(value)}
        </div>
        {stage && (
          <>
            <div className="fs--1 text-600 mb-2">
              {loading ? '—' : `${percent}% de los registrados`}
            </div>
            <Progress value={loading ? 0 : percent} color={color} className="mb-2" style={{ height: 5 }} />
          </>
        )}
        {note && <div className="fs--1 text-500 mt-auto">{note}</div>}
      </CardBody>
    </div>
  );
};

const PaymentsDistribution = ({ payments, registered }) => {
  const { total, owners, condominiums, top, top3_share: top3Share } = payments;
  const topShare = Math.round((top3Share || 0) * 100);
  const maxTop = top.length ? top[0].total : 0;
  const concentrated = condominiums > 0 && condominiums < registered && topShare >= 60;

  return (
    <Card className="h-100 shadow-none border">
      <FalconCardHeader
        titleTag="h6"
        light={false}
        title={(
          <>
            Distribución de los pagos
            {concentrated && <Badge color="soft-warning" pill className="ml-2">Concentrados</Badge>}
          </>
        )}
      />
      <CardBody className="fs--1">
        {total === 0 ? (
          <p className="mb-0 text-600">No se reportaron pagos en el periodo seleccionado.</p>
        ) : (
          <>
            <p className="mb-3">
              <b>{formatNumber(total)}</b> pagos reportados por <b>{formatNumber(owners)}</b> propietarios en{' '}
              <b>{formatNumber(condominiums)}</b> {condominiums === 1 ? 'condominio' : 'condominios'}
              {' '}({percentOf(condominiums, registered)}% de los registrados).
              {' '}Los {Math.min(3, top.length)} con más pagos concentran el <b>{topShare}%</b>.
            </p>
            {top.map((condominium) => (
              <div key={condominium.id} className="mb-2">
                <div className="d-flex justify-content-between">
                  <span className="text-truncate mr-2">{condominium.name}</span>
                  <b>{formatNumber(condominium.total)}</b>
                </div>
                <Progress value={percentOf(condominium.total, maxTop)} color="success" style={{ height: 4 }} />
              </div>
            ))}
          </>
        )}
      </CardBody>
    </Card>
  );
};

const OnboardingBreakdown = ({ steps, registered }) => (
  <Card className="h-100 shadow-none border">
    <FalconCardHeader title="Pasos de la carga inicial" titleTag="h6" light={false} />
    <CardBody className="fs--1">
      <p className="text-600 mb-3">Condominios que tienen resuelto cada paso de la guía de primeros pasos.</p>
      {Object.keys(ADOPTION_STEP_LABELS).map((step) => (
        <div key={step} className="mb-2">
          <div className="d-flex justify-content-between">
            <span>{ADOPTION_STEP_LABELS[step]}</span>
            <span>
              <b>{formatNumber(steps[step] || 0)}</b>
              <span className="text-500"> / {formatNumber(registered)}</span>
            </span>
          </div>
          <Progress value={percentOf(steps[step] || 0, registered)} color="primary" style={{ height: 4 }} />
        </div>
      ))}
    </CardBody>
  </Card>
);

const IndicatorsAdoptionFunnel = ({ summary, loading, error, selectedStage, onSelectStage }) => {
  const registered = summary?.registered || 0;
  const payments = summary?.payments || { total: 0, owners: 0, condominiums: 0, top: [], top3_share: 0 };
  const owners = summary?.owners || { total: 0, active: 0, active_window_days: 15 };

  const stages = [
    {
      stage: null,
      title: 'Condominios registrados',
      value: registered,
      color: 'dark',
      note: 'Toca una etapa para ver los condominios que aún no la cumplen.',
    },
    {
      stage: 'onboarding',
      title: 'Carga inicial completa',
      value: summary?.onboarded || 0,
      color: 'primary',
      note: 'Los 5 pasos de la guía del administrador.',
    },
    {
      stage: 'planilla',
      title: 'Con planilla emitida',
      value: summary?.with_planilla || 0,
      color: 'info',
      note: 'Al menos una planilla con cuotas por cobrar.',
    },
    {
      stage: 'active_owners',
      title: 'Con propietarios activos',
      value: summary?.with_active_owners || 0,
      color: 'warning',
      note: `${owners.active} de ${owners.total} propietarios entraron en los últimos ${owners.active_window_days} días.`,
    },
    {
      stage: 'payments',
      title: 'Con pagos reportados',
      value: summary?.with_payments || 0,
      color: 'success',
      note: `${payments.total} pagos en el periodo seleccionado.`,
    },
  ];

  return (
    <>
      <Row className="mb-3">
        {stages.map((item) => (
          <Col key={item.stage || 'registered'} xs={12} sm={6} lg={4} xl className="mb-3 mb-xl-0">
            <StageCard
              {...item}
              registered={registered}
              loading={loading}
              selected={selectedStage === item.stage}
              onSelect={onSelectStage}
            />
          </Col>
        ))}
      </Row>
      {error && (
        <Alert color="danger" className="mb-3">
          No pudimos cargar los indicadores de adopción. Intenta nuevamente en unos segundos.
        </Alert>
      )}
      {!loading && !error && summary && (
        <Row className="mb-3">
          <Col lg={6} className="mb-3 mb-lg-0">
            <PaymentsDistribution payments={payments} registered={registered} />
          </Col>
          <Col lg={6}>
            <OnboardingBreakdown steps={summary.onboarding_steps || {}} registered={registered} />
          </Col>
        </Row>
      )}
    </>
  );
};

export default IndicatorsAdoptionFunnel;
